/**
 * Reconnect refresh flow — OURS, not AuthManager's (app uses getToken mode).
 *
 * One in-flight promise dedupes concurrent 401s so they don't stampede the
 * refresh endpoint. `getRefreshToken` resolves the decrypted refresh token —
 * which may itself require showing the LockScreen (the unlock ceremony) if the
 * key isn't in memory this session. The caller (interceptor / gate) supplies it.
 */
import { config } from '../config';

type PostFn = (url: string, body: unknown) => Promise<{ data: any }>;

let inflight: Promise<string> | null = null;

/** Test seam. */
export function __resetInflightForTest() {
  inflight = null;
}

/**
 * Drop the shared in-flight refresh promise. Called on logout so a NEW caller
 * (the next user in the same tab after a soft logout) cannot piggyback on the
 * PRIOR user's shared refresh POST. NOTE: this does not abort an already-running
 * fetch — a truly mid-flight refresh can still write its access token — but it
 * prevents a fresh 401 from reusing the stale shared promise. Public alias of
 * the test seam.
 */
export const resetReconnect = __resetInflightForTest;

/**
 * Exchange a refresh token for a fresh access token and persist it.
 * Concurrent callers share one POST. On failure the in-flight slot is cleared
 * so the next attempt can retry.
 */
export function refreshAccessTokenWith(
  getRefreshToken: () => Promise<string>,
  post: PostFn,
): Promise<string> {
  if (inflight) return inflight;
  inflight = (async () => {
    const refreshToken = await getRefreshToken();
    const res = await post('/auth/refresh', { refresh_token: refreshToken });
    const accessToken: string | undefined = res?.data?.access_token;
    if (!accessToken) throw new Error('Refresh response missing access_token');
    // Fixed refresh token: ignore any refresh_token in the response.
    localStorage.setItem(config.auth.storageKey, accessToken);
    return accessToken;
  })();
  inflight.catch(() => {}).finally(() => {
    inflight = null;
  });
  return inflight;
}
